
import React from 'react';

export const ClosingCostsBreakdown: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Detailed Closing Costs Breakdown
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding every component of closing costs helps you budget accurately and identify potential savings 
            opportunities in your apartment building transaction.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/f42efa2fb5bd01e118bcfed9033dc965.jpg"
              alt="Detailed closing costs breakdown and settlement statement"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Typical Closing Cost Categories</h3>
              
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-red-800">Real Estate Commissions</h4>
                    <span className="font-bold text-red-600">6-10%</span>
                  </div>
                  <p className="text-red-700 text-sm">Usually the largest closing expense, split between listing and buyer agents</p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-blue-800">Title Services</h4>
                    <span className="font-bold text-blue-600">0.5-1.2%</span>
                  </div>
                  <p className="text-blue-700 text-sm">Title insurance, search, and closing services</p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-green-800">Government Fees</h4>
                    <span className="font-bold text-green-600">0.1-2.5%</span>
                  </div>
                  <p className="text-green-700 text-sm">Transfer taxes, recording fees, and municipal charges</p>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded-r-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-purple-800">Professional Services</h4>
                    <span className="font-bold text-purple-600">0.5-1.5%</span>
                  </div>
                  <p className="text-purple-700 text-sm">Attorney, accountant, and survey costs</p>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-yellow-800">Miscellaneous</h4>
                    <span className="font-bold text-yellow-600">0.2-0.8%</span>
                  </div>
                  <p className="text-yellow-700 text-sm">Courier, wire transfers, and administrative fees</p>
                </div>
              </div>

              <div className="mt-6 bg-gray-900 text-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">Total Closing Costs</span>
                  <span className="text-xl font-bold">7-16%</span>
                </div>
                <p className="text-gray-300 text-sm mt-1">Percentage of sale price varies by location</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Itemized Closing Cost Examples</h3>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="text-xl font-semibold text-gray-900 mb-6 text-center">$1,000,000 Sale Example</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Listing Agent Commission (4%)</span>
                  <span className="font-medium">$40,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Buyer Agent Commission (4%)</span>
                  <span className="font-medium">$40,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Title Insurance</span>
                  <span className="font-medium">$3,500</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Escrow Services</span>
                  <span className="font-medium">$2,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Transfer Tax (1%)</span>
                  <span className="font-medium">$10,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Attorney Fees</span>
                  <span className="font-medium">$5,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Survey</span>
                  <span className="font-medium">$3,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Recording Fees</span>
                  <span className="font-medium">$200</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Wire Transfer Fees</span>
                  <span className="font-medium">$150</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Miscellaneous Fees</span>
                  <span className="font-medium">$350</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-3 border-t-2 border-gray-300">
                  <span>Total Closing Costs</span>
                  <span className="text-red-600">$104,200</span>
                </div>
                <div className="text-center text-gray-600 text-xs">
                  (10.42% of sale price)
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="text-xl font-semibold text-gray-900 mb-6 text-center">$2,500,000 Sale Example</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Listing Agent Commission (3%)</span>
                  <span className="font-medium">$75,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Buyer Agent Commission (3%)</span>
                  <span className="font-medium">$75,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Title Insurance</span>
                  <span className="font-medium">$7,500</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Escrow Services</span>
                  <span className="font-medium">$4,500</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Transfer Tax (0.8%)</span>
                  <span className="font-medium">$20,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Attorney Fees</span>
                  <span className="font-medium">$8,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Survey</span>
                  <span className="font-medium">$5,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Recording Fees</span>
                  <span className="font-medium">$300</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Wire Transfer Fees</span>
                  <span className="font-medium">$200</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-100">
                  <span className="text-gray-700">Miscellaneous Fees</span>
                  <span className="font-medium">$500</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-3 border-t-2 border-gray-300">
                  <span>Total Closing Costs</span>
                  <span className="text-red-600">$196,000</span>
                </div>
                <div className="text-center text-gray-600 text-xs">
                  (7.84% of sale price)
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-teal-50 p-6 rounded-xl">
            <h4 className="text-lg font-semibold text-teal-800 mb-3 text-center">Key Observations</h4>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-600 mb-1">Lower %</div>
                <p className="text-teal-700">Higher-value properties typically have lower percentage closing costs</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-600 mb-1">Negotiable</div>
                <p className="text-teal-700">Many fees are negotiable, especially on larger transactions</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-600 mb-1">Variable</div>
                <p className="text-teal-700">Costs vary significantly by location and transaction complexity</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cost-Saving Strategies</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-percent-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Negotiate Commission Rates</h4>
                    <p className="text-gray-600 text-sm">
                      Commercial properties often qualify for reduced rates. On larger transactions, agents may 
                      accept lower percentages due to higher dollar amounts.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-shopping-bag-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Shop Title Companies</h4>
                    <p className="text-gray-600 text-sm">
                      Get quotes from multiple title companies. Rates can vary by hundreds or thousands of dollars, 
                      especially on high-value properties.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-team-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Bundle Services</h4>
                    <p className="text-gray-600 text-sm">
                      Some companies offer package deals for title, escrow, and related services. 
                      Bundling can result in overall savings.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-calendar-line text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Time Your Closing</h4>
                    <p className="text-gray-600 text-sm">
                      End-of-month closings may incur higher fees. Mid-month closings often have more 
                      scheduling flexibility and competitive pricing.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-green-50 p-4 rounded-lg">
                <h5 className="font-semibold text-green-800 mb-2">Potential Savings</h5>
                <p className="text-green-700 text-sm">
                  Strategic cost management can save 1-3% of your sale price, translating to $10,000-75,000 
                  on typical apartment building transactions.
                </p>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=cost%20saving%20strategies%20negotiation%20meeting%20for%20real%20estate%20closing%20costs%2C%20professional%20handshake%20and%20contract%20discussion%2C%20apartment%20building%20transaction%20cost%20reduction%20consultation&width=600&height=400&seq=closing-cost-savings&orientation=landscape"
              alt="Cost-saving strategies and negotiation for closing costs"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
